<script setup lang="ts">
import { create } from "domain";
import { inject, ref, onMounted } from "vue";

const { lead, description, categories, brands, image } = defineProps({
    lead: {
        type: String,
        required: true,
    },
    description: {
        type: String,
        required: true,
    },
    categories: {
        type: Array,
        required: true,
    },
    brands: {
        type: Array,
        required: true,
    },
    image: {
        type: String,
        required: true,
    },
});

const filteredBrands = ref([...brands]);
const brandsByLetters = ref([]);
const mobileBrandsByLetters = ref([]);
const smallMobileBrandsByLetters = ref([]);

const activeFilter = ref<string | number | null>(null);

const buttons = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
    "0-9",
    "All",
];

onMounted(() => {
    createBrandGroups();
});

const filterBrands = (letter: string) => {
    activeFilter.value = letter;

    if (letter == "All") {
        activeFilter.value = null;

        filteredBrands.value = [...brands];

        createBrandGroups();
        scrollToResults();

        return;
    }

    if (letter == "0-9") {
        filteredBrands.value = brands.filter((brand) => {
            return !isNaN(parseInt(brand.title.charAt(0)));
        });

        createBrandGroups();
        scrollToResults();

        return;
    }

    filteredBrands.value = brands.filter((brand) => {
        return brand.title.charAt(0).toUpperCase() == letter;
    });

    createBrandGroups();

    scrollToResults();
};

const scrollToResults = () => {
    const element = document.querySelector(".brand-results") as HTMLElement;

    if (element) {
        window.scrollTo({
            top: element.offsetTop - 180,
            behavior: "smooth",
        });
    }
};

const filterByCategory = (categoryId: number) => {
    activeFilter.value = categoryId;

    filteredBrands.value = brands.filter((brand) => {
        return brand.category.id == categoryId;
    });
    createBrandGroups();

    scrollToResults();
};

const searchBrands = (event: Event) => {
    activeFilter.value = null;

    const target = event.target as HTMLInputElement;
    const value = target.value.toLowerCase();

    filteredBrands.value = brands.filter((brand) => {
        return brand.title.toLowerCase().includes(value);
    });

    createBrandGroups();
};

const createBrandGroups = () => {
    brandsByLetters.value = [];
    mobileBrandsByLetters.value = [];
    smallMobileBrandsByLetters.value = [];

    let currentIndex = 0;
    let currentLetter = "";

    let colCount = 0;

    filteredBrands.value.forEach((brand) => {
        const firstLetter = brand.title.charAt(0).toUpperCase();

        if (currentLetter == "") {
            currentLetter = firstLetter;
        }

        if (colCount >= 12 || firstLetter != currentLetter) {
            currentIndex++;

            if (firstLetter != currentLetter) {
                currentLetter = firstLetter;
            }

            if (colCount >= 12) {
                colCount = 0;
            }
        }

        if (brandsByLetters.value[currentIndex] == undefined) {
            brandsByLetters.value[currentIndex] = {
                letter: currentLetter,
                brands: [],
            };
        }

        brandsByLetters.value[currentIndex].brands.push(brand);
        colCount++;
    });

    let mobileCurrentIndex = 0;
    let mobileCurrentLetter = "";

    let mobileColCount = 0;

    filteredBrands.value.forEach((brand) => {
        const mobileFirstLetter = brand.title.charAt(0).toUpperCase();

        if (mobileCurrentLetter == "") {
            mobileCurrentLetter = mobileFirstLetter;
        }

        if (mobileColCount >= 4 || mobileFirstLetter != mobileCurrentLetter) {
            mobileCurrentIndex++;

            if (mobileFirstLetter != mobileCurrentLetter) {
                mobileCurrentLetter = mobileFirstLetter;
            }

            if (mobileColCount >= 4) {
                mobileColCount = 0;
            }
        }

        if (mobileBrandsByLetters.value[mobileCurrentIndex] == undefined) {
            mobileBrandsByLetters.value[mobileCurrentIndex] = {
                letter: mobileCurrentLetter,
                brands: [],
            };
        }

        mobileBrandsByLetters.value[mobileCurrentIndex].brands.push(brand);
        mobileColCount++;
    });

    let smallMobileCurrentIndex = 0;
    let smallMobileCurrentLetter = "";

    let smallMobileColCount = 0;

    filteredBrands.value.forEach((brand) => {
        const smallMobileFirstLetter = brand.title.charAt(0).toUpperCase();

        if (smallMobileCurrentLetter == "") {
            smallMobileCurrentLetter = smallMobileFirstLetter;
        }

        if (
            smallMobileColCount >= 2 ||
            smallMobileFirstLetter != smallMobileCurrentLetter
        ) {
            smallMobileCurrentIndex++;

            if (smallMobileFirstLetter != smallMobileCurrentLetter) {
                smallMobileCurrentLetter = smallMobileFirstLetter;
            }

            if (smallMobileColCount >= 2) {
                smallMobileColCount = 0;
            }
        }

        if (
            smallMobileBrandsByLetters.value[smallMobileCurrentIndex] ==
            undefined
        ) {
            smallMobileBrandsByLetters.value[smallMobileCurrentIndex] = {
                letter: smallMobileCurrentLetter,
                brands: [],
            };
        }

        smallMobileBrandsByLetters.value[smallMobileCurrentIndex].brands.push(
            brand,
        );
        smallMobileColCount++;
    });
};
</script>
<template>
    <div class="grid grid-cols-24 grid-rows-3 mb-24">
        <div class="col-start-1 col-span-24 row-span-2 row-start-1">
            <div
                class="w-full h-full bg-cover bg-center bg-darken-80"
                :style="{ backgroundImage: 'url(' + image + ')' }"
            ></div>
        </div>

        <div
            class="row-start-1 row-span-1 col-start-3 col-end-23 md:col-end-12 color-white pt-20 pb-8 z-30"
        >
            <h1 class="h1 text-white leading-none">{{ lead }}</h1>
        </div>

        <div
            class="col-start-3 col-end-23 row-start-2 row-span-2 bg-white rounded-lg px-12 md:px-28 py-12 md:py-16 shadow-strong flex flex-col gap-10 z-30"
        >
            <div class="w-full md:w-1/2">{{ description }}</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="grid md:grid-cols-3 gap-4">
                    <button
                        v-for="category in categories"
                        @click="filterByCategory(category.id)"
                        class="btn btn-light-grey flex items-center gap-4"
                        :class="{ 'bg-grey-200': activeFilter === category.id }"
                    >
                        <svg
                            width="11"
                            height="19"
                            viewBox="0 0 11 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M1 17.1352L9 9.13519L0.999999 1.13519"
                                :stroke="category.color"
                                stroke-width="2.66667"
                            />
                        </svg>

                        <div>
                            {{ category.title }}
                        </div>
                    </button>
                </div>
                <div class="relative min-h-12">
                    <input
                        type="search"
                        class="w-full h-full bg-grey-50 rounded-md px-7"
                        placeholder="Search for a brand"
                        @input="searchBrands"
                    />
                    <i
                        class="fal fa-search text-f4 absolute right-6 top-1/2 -translate-y-1/2"
                    ></i>
                </div>
            </div>
            <div
                class="grid grid-cols-4 sm:grid-cols-7 xl:grid-cols-14 gap-4 md:gap-8"
            >
                <div
                    v-for="button in buttons"
                    @click="filterBrands(button)"
                    class="flex items-center justify-center"
                >
                    <button
                        class="btn btn-light-grey w-12 h-12 flex items-center justify-center text-nowrap"
                        :class="{ 'bg-grey-200': activeFilter === button }"
                    >
                        {{ button }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div class="grid grid-cols-24 mb-32">
        <div class="col-start-3 col-end-23 grid-cols-12 gap-8 hidden xl:grid">
            <div
                v-for="letterGroup in brandsByLetters"
                :class="
                    'grid gap-x-8 gap-y-2 grid-cols-' +
                    letterGroup.brands.length +
                    ' col-span-' +
                    letterGroup.brands.length
                "
            >
                <div
                    class="font-bold text-f4 border-b border-grey-200 mb-2 pb-2"
                    :class="'col-span-' + letterGroup.brands.length"
                >
                    {{ letterGroup.letter }}
                </div>

                <a
                    :href="brand.url"
                    v-for="brand in letterGroup.brands"
                    class="h-40"
                >
                    <div class="bg-white rounded-lg py-1 mb-2">
                        <div
                            :style="'background-image: url(' + brand.logo + ')'"
                            :alt="brand.title"
                            class="w-full h-14 bg-contain bg-center bg-no-repeat"
                        ></div>
                    </div>
                    <div>{{ brand.title }}</div>
                </a>
            </div>
        </div>

        <div
            class="col-start-3 col-end-23 grid-cols-4 gap-4 xl:hidden md:grid hidden brand-results"
        >
            <div
                v-for="letterGroup in mobileBrandsByLetters"
                :class="
                    'grid gap-x-4 md:gap-x-8 gap-y-2 grid-cols-' +
                    letterGroup.brands.length +
                    ' col-span-' +
                    letterGroup.brands.length
                "
            >
                <div
                    class="font-bold text-f4 border-b border-grey-200"
                    :class="'col-span-' + letterGroup.brands.length"
                >
                    {{ letterGroup.letter }}
                </div>

                <a
                    :href="brand.url"
                    v-for="brand in letterGroup.brands"
                    class="h-40"
                >
                    <div class="bg-white rounded-lg py-1 mb-2">
                        <div
                            :style="'background-image: url(' + brand.logo + ')'"
                            :alt="brand.title"
                            class="w-full h-14 bg-contain bg-center bg-no-repeat"
                        ></div>
                    </div>
                    <div>{{ brand.title }}</div>
                </a>
            </div>
        </div>

        <div
            class="col-start-3 col-end-23 grid-cols-2 gap-2 md:gap-8 md:hidden grid"
        >
            <div
                v-for="letterGroup in smallMobileBrandsByLetters"
                :class="
                    'grid gap-x-4 md:gap-x-8 gap-y-2 grid-cols-' +
                    letterGroup.brands.length +
                    ' col-span-' +
                    letterGroup.brands.length
                "
            >
                <div
                    class="font-bold text-f4 border-b border-grey-200"
                    :class="'col-span-' + letterGroup.brands.length"
                >
                    {{ letterGroup.letter }}
                </div>

                <a
                    :href="brand.url"
                    v-for="brand in letterGroup.brands"
                    class="h-32"
                >
                    <div class="bg-white rounded-lg py-1 mb-2">
                        <div
                            :style="'background-image: url(' + brand.logo + ')'"
                            :alt="brand.title"
                            class="w-full h-14 bg-contain bg-center bg-no-repeat"
                        ></div>
                    </div>
                    <div>{{ brand.title }}</div>
                </a>
            </div>
        </div>
    </div>
</template>
